import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getStudents, getStudent } from "../../../services/admin.services";
import * as XLSX from "xlsx";
import { Download, ArrowUpDown, Search, Filter } from "lucide-react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { setAllStudents } from "../../../state/reducers/appSlice";

const isStudentArchived = (student) =>
  student?.loan_types && student.loan_types.__archived__ === true;

function StatusTrackingTable() {
  const dispatch = useDispatch();
  const allStudents = useSelector((state) => state.app.allStudents);
  const allStudentsLoaded = useSelector(
    (state) => state.app.allStudentsLoaded
  );

  const students = useMemo(
    () =>
      allStudents.filter(
        (s) => !isStudentArchived(s) && (s?.status || "") !== "complete"
      ),
    [allStudents]
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage, setStudentsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "username",
    direction: "ascending",
  });
  const [filters, setFilters] = useState({
    status: "",
    city: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (allStudentsLoaded) {
      setLoading(false);
      return;
    }
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const [studentsRes, usersRes] = await Promise.all([
          getStudents(),
          getStudent(),
        ]);
        const userMap = {};
        usersRes.data.data.forEach((user) => {
          userMap[user.id] = user;
        });
        const allMerged = studentsRes.data.data.map((student) => ({
          ...student,
          user: userMap[student.student_id] || {},
        }));
        dispatch(setAllStudents(allMerged));
      } catch (error) {
        console.error("Error fetching students:", error);
        toast.error("Failed to load student data");
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, [dispatch, allStudentsLoaded]);

  useEffect(() => {
    const statuses = [
      ...new Set(students.map((s) => s.status).filter(Boolean)),
    ];
    const cities = [
      ...new Set(students.map((s) => s.city).filter(Boolean)),
    ];
    setAvailableStatuses(statuses);
    setAvailableCities(cities);
  }, [students]);

  // Navigate to detail page
  const handleRowClick = (studentId) => {
    navigate(`/admin/application/status/student/${studentId}`);
  };

  // Sort function
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: "",
      city: "",
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Export to Excel
  const handleDownloadExcel = () => {
    try {
      if (filteredStudents.length === 0) {
        toast.error("No data available to export");
        return;
      }

      const formattedData = filteredStudents.map((student, index) => ({
        SrNo: index + 1,
        StudentName: student.username || "",
        Email: student.email || "",
        MobileNumber: student.phone_number || "",
        Location: student.city || "",
        Status: student.status || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);

      // Set column widths
      const widths = [
        { wch: 5 }, // Sr No
        { wch: 20 }, // Student Name
        { wch: 30 }, // Email
        { wch: 15 }, // Mobile Number
        { wch: 15 }, // Location
        { wch: 18 }, // Status
      ];

      worksheet["!cols"] = widths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Students");

      XLSX.writeFile(
        workbook,
        `status_tracking_students_${
          new Date().toISOString().split("T")[0]
        }.xlsx`
      );
      toast.success("Excel file downloaded successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  // Get status badge color
  const getStatusBadgeClass = (status) => {
    if (!status) return "bg-gray-100 text-gray-800";

    const statusLower = status.toLowerCase();
    if (statusLower.includes("complete")) {
      return "bg-green-100 text-green-800";
    } else if (
      statusLower.includes("document_pending") ||
      statusLower.includes("pending")
    ) {
      return "bg-yellow-100 text-yellow-800";
    } else if (statusLower.includes("university")) {
      return "bg-blue-100 text-blue-800";
    } else if (
      statusLower.includes("rejected") ||
      statusLower.includes("failed")
    ) {
      return "bg-red-100 text-red-600";
    } else {
      return "bg-gray-100 text-gray-800";
    }
  };

  // Filter and sort students
  const filteredStudents = useMemo(() => {
    if (!Array.isArray(students)) return [];

    let result = students;

    // Apply text search
    if (searchTerm) {
      result = result.filter(
        (student) =>
          student.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          false ||
          student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          false ||
          student.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          false ||
          student.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          false ||
          student.phone_number?.includes(searchTerm) ||
          false
      );
    }

    // Apply filters
    if (filters.status) {
      result = result.filter(
        (student) =>
          student.status?.toLowerCase() === filters.status.toLowerCase()
      );
    }

    if (filters.city) {
      result = result.filter(
        (student) => student.city?.toLowerCase() === filters.city.toLowerCase()
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        // Handle null/undefined values
        const valA = a[sortConfig.key] || "";
        const valB = b[sortConfig.key] || "";

        // For string comparison
        if (typeof valA === "string" && typeof valB === "string") {
          const comparison = valA.localeCompare(valB, undefined, {
            sensitivity: "base",
          });
          return sortConfig.direction === "ascending"
            ? comparison
            : -comparison;
        }

        // For non-string comparison
        if (valA < valB) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (valA > valB) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [students, searchTerm, sortConfig, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  );

  // For the mobile view - determine if any filters are active
  const isFiltering = filters.status || filters.city || searchTerm;

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      {/* Header with search and export */}
      <div className="mb-5 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <h2 className="text-xl font-semibold text-slate-800">
          Status & Tracking
          {students.length > 0 && (
            <span className="ml-2 text-sm font-normal text-slate-500">
              ({filteredStudents.length} of {students.length} students)
            </span>
          )}
        </h2>

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {/* Search box */}
          <div className="relative w-full sm:w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={18} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search students..."
              className="w-full rounded-xl border border-slate-300 py-2 pl-10 pr-4 text-slate-700 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
            />
          </div>

          {/* Filter button (visible on mobile) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="sm:hidden flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
          >
            <Filter
              size={18}
              className={isFiltering ? "text-green-500" : "text-gray-500"}
            />
            <span className="ml-2">Filters</span>
            {isFiltering && (
              <span className="ml-1 w-2 h-2 bg-green-500 rounded-full"></span>
            )}
          </button>

          {/* Export button */}
          <button
            onClick={handleDownloadExcel}
            className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 font-medium text-white transition-colors hover:bg-indigo-700"
            disabled={filteredStudents.length === 0}
          >
            <Download size={18} />
            <span>Export Excel</span>
          </button>
        </div>
      </div>

      {/* Filter bar (can be toggled on mobile) */}
      <div
        className={`mb-4 rounded-xl bg-slate-50 p-4 ${
          showFilters ? "block" : "hidden sm:block"
        }`}
      >
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex flex-col w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500"
            >
              <option value="">All Statuses</option>
              {availableStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <select
              value={filters.city}
              onChange={(e) => {
                setFilters({ ...filters, city: e.target.value });
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500"
            >
              <option value="">All Locations</option>
              {availableCities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col w-full sm:w-auto sm:mt-6">
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
              disabled={!isFiltering}
            >
              Clear Filters
            </button>
          </div>

          <div className="ml-auto self-center hidden sm:block">
            <label className="flex items-center text-sm text-gray-600">
              <span className="mr-2">Show</span>
              <select
                value={studentsPerPage}
                onChange={(e) => {
                  setStudentsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="ml-2">entries</span>
            </label>
          </div>
        </div>
      </div>

      {/* Records per page selector (visible on mobile) */}
      <div className="mb-4 flex justify-end sm:hidden">
        <label className="flex items-center text-sm text-gray-600">
          <span className="mr-2">Show</span>
          <select
            value={studentsPerPage}
            onChange={(e) => {
              setStudentsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded px-2 py-1"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span className="ml-2">entries</span>
        </label>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 bg-white">
          <thead>
            <tr className="bg-slate-100/80 text-sm font-medium text-slate-700">
              <th className="py-3 px-6 text-left">Sr No</th>
              <th
                className="py-3 px-6 text-left cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort("username")}
              >
                <div className="flex items-center gap-1">
                  Student Name
                  <ArrowUpDown
                    size={16}
                    className={
                      sortConfig.key === "username"
                        ? "text-green-500"
                        : "text-gray-400"
                    }
                  />
                </div>
              </th>
              <th
                className="py-3 px-6 text-left cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort("email")}
              >
                <div className="flex items-center gap-1">
                  Email
                  <ArrowUpDown
                    size={16}
                    className={
                      sortConfig.key === "email"
                        ? "text-green-500"
                        : "text-gray-400"
                    }
                  />
                </div>
              </th>
              <th className="py-3 px-6 text-left hidden sm:table-cell">
                Mobile Number
              </th>
              <th
                className="py-3 px-6 text-left hidden md:table-cell cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort("city")}
              >
                <div className="flex items-center gap-1">
                  Location
                  <ArrowUpDown
                    size={16}
                    className={
                      sortConfig.key === "city"
                        ? "text-green-500"
                        : "text-gray-400"
                    }
                  />
                </div>
              </th>
              <th
                className="py-3 px-6 text-left cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort("status")}
              >
                <div className="flex items-center gap-1">
                  Status
                  <ArrowUpDown
                    size={16}
                    className={
                      sortConfig.key === "status"
                        ? "text-green-500"
                        : "text-gray-400"
                    }
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="6" className="py-8 px-6 text-center">
                  <div className="flex flex-col justify-center items-center space-y-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                    <span className="text-base text-gray-500">
                      Loading students...
                    </span>
                  </div>
                </td>
              </tr>
            ) : currentStudents.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-8 px-6 text-center">
                  {isFiltering ? (
                    <div className="text-gray-500">
                      <p className="font-medium text-base mb-2">
                        No matching records found
                      </p>
                      <p className="text-base">
                        Try adjusting your search or filters
                      </p>
                      <button
                        onClick={clearFilters}
                        className="mt-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium"
                      >
                        Clear all filters
                      </button>
                    </div>
                  ) : (
                    <p className="font-medium text-base text-gray-500">
                      No students available
                    </p>
                  )}
                </td>
              </tr>
            ) : (
              currentStudents.map((student, index) => (
                <tr
                  key={student.student_id || index}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleRowClick(student.student_id)}
                >
                  <td className="py-4 px-6 text-sm font-medium text-gray-700">
                    {indexOfFirstStudent + index + 1}
                  </td>
                  <td className="py-4 px-6 text-sm font-medium text-gray-900">
                    {student.username || "—"}
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-700">
                    {student.email || "—"}
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-700 hidden sm:table-cell">
                    {student.phone_number || "—"}
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-700 hidden md:table-cell">
                    {student.city || "—"}
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`px-3 py-1 inline-flex text-sm font-medium rounded-full ${getStatusBadgeClass(
                        student.status
                      )}`}
                    >
                      {student.status || "Not Set"}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      {!loading && totalPages > 0 && (
        <div className="mt-4 flex flex-col sm:flex-row justify-between items-center">
          <div className="text-sm text-gray-600 mb-3 sm:mb-0">
            Showing {indexOfFirstStudent + 1} to{" "}
            {Math.min(indexOfLastStudent, filteredStudents.length)} of{" "}
            {filteredStudents.length} entries
          </div>

          <div className="flex">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="mx-1 px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <div className="hidden sm:flex">
              {Array.from(
                { length: Math.min(totalPages, 5) },
                (_, i) => i + 1
              ).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`mx-1 px-4 py-2 border rounded-md ${
                    currentPage === page
                      ? "bg-green-50 text-green-600 border-green-200 font-medium"
                      : "bg-white hover:bg-gray-50 border-gray-300"
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>

            <div className="flex sm:hidden items-center px-4">
              <span className="text-sm text-gray-700">
                {currentPage} / {totalPages}
              </span>
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || totalPages === 0}
              className="mx-1 px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatusTrackingTable;
